/**
 * Axios Request Wrapper
 * ---------------------
 *
 * @author  Sheharyar Naseer (@sheharyarn)
 * @license MIT
 *
 */

import axios from "axios";
import {
  getAuthorizationHeader,
  getBusinessAuthorizationHeader,
} from "./cookie";
import { getCFCountry, getCFIP, getPaymentCountry } from "./setCookie";
import { annotateRateLimitResponse } from "./rateLimit";

const url = process.env.API_URL;

const shouldUseLocalHttpsAgent = (baseURL) => (
  typeof window === "undefined"
  && /^https:\/\/[^/]+\.test(?::\d+)?/i.test(baseURL || "")
);

// One TCP connect costs a SYN, and SYNs get dropped. Measured from the
// production web host on 19 AUG 26: ~30% of fresh connects to the Cloudflare
// edge stall exactly 1.07s (one SYN retransmit) and one took 7.3s, while ICMP
// showed 0% loss and the NIC reported zero errors. A checkout that opens a new
// connection every time is playing that lottery on every click; reusing a warm
// socket skips the draw entirely.
let sharedKeepAliveAgent;

const getServerHttpsAgent = (baseURL) => {
  if (typeof window !== "undefined") {
    return undefined;
  }

  if (shouldUseLocalHttpsAgent(baseURL)) {
    // Local HERD .test certificates are trusted by the browser, but not always
    // by the Node process that runs Next API proxies.
    // eslint-disable-next-line global-require
    const https = require("https");
    return new https.Agent({ rejectUnauthorized: false });
  }

  if (!sharedKeepAliveAgent) {
    // eslint-disable-next-line global-require
    const https = require("https");
    sharedKeepAliveAgent = new https.Agent({
      keepAlive: true,
      keepAliveMsecs: 15000,
      maxSockets: 64,
      maxFreeSockets: 16,
    });
  }

  return sharedKeepAliveAgent;
};

// Without a timeout a hung connect blocks until the OS gives up, and nginx in
// front returns 504 first. That is what a buyer saw on 19 AUG 26: the checkout
// hung past 60s, nginx cut it, and this process only learned at 72s. Failing
// fast leaves room to retry INSIDE nginx's window instead of after it.
const DEFAULT_TIMEOUT_MS = Number(process.env.SERVER_REQUEST_TIMEOUT_MS) || 20000;

/**
 * Request Wrapper with default success/error actions
 */
const request = (options) => {
  /**
   * Create an Axios Client with defaults
   */


  const requestHeaders = options.customHeaders || {
    "Content-type": "application/json",
    "Cache-Control":
      "no-cache, no-store, must-revalidate, post-check=0, pre-check=0",
    Pragma: "no-cache",
    Expires: "0",
  };

  // Clone so we never mutate the caller's headers object — on the single SSR
  // Node process a shared object can leak one user's CF country/IP onto another
  // user's outbound request.
  const additionalHeaders = { ...(options.headers || {}) };

  const authorizationHeader = getAuthorizationHeader(options.ctx);
  const businessHeader = getBusinessAuthorizationHeader(options.ctx);


  if (!additionalHeaders["x-http-ipcountry"]) {
    const cfConfig = {
      config_country: getCFCountry(options.ctx),
      config_IP: getCFIP(options.ctx)
    };
    if (cfConfig.config_country) {
      additionalHeaders["x-http-ipcountry"] = cfConfig.config_country;
      additionalHeaders["x-http-connecting-ip"] = cfConfig.config_IP;
    }
  }
  if (!additionalHeaders["x-client-country-code"] || typeof additionalHeaders["x-client-country-code"] !== "string") {
    const payment_country = getPaymentCountry(options.ctx);
    if (typeof payment_country === "string") {
      additionalHeaders["x-client-country-code"] = payment_country;
    }
  }


  // console.log("CHECK EXTERNAL PARAMS:", options.params);
  // console.log("CHECK EXTERNAL API:", options.url, additionalHeaders);
  const baseURL = options.MAIN_URL || url;
  const client = axios.create({
    baseURL,
    timeout: options.timeout || DEFAULT_TIMEOUT_MS,
    httpsAgent: getServerHttpsAgent(baseURL),
    headers: {
      ...requestHeaders,
      ...authorizationHeader,
      ...businessHeader,
      ...additionalHeaders,
      ...{
        "X-Platform": "web",
      },
    },
  });


  // eslint-disable-next-line arrow-body-style
  const onSuccess = (response) => {
    // console.debug('Request Successful!', response);
    return response.data;
  };

  const onError = (error) => {
    const failed = (error && error.config) || {};
    const status = error && error.response && error.response.status;
    console.error(
      "Request Failed:",
      String(failed.method || options.method || "GET").toUpperCase(),
      String(failed.baseURL || "") + String(failed.url || options.url || ""),
      status ? `status=${status}` : (error && error.message) || "unknown"
    );

    if (error.response) {
      // Request was made but server responded with something
      // other than 2xx
      // console.log('Status:', error.response.status);
      // console.log('Data:', error.response.data);
      // console.log('Headers:', error.response.headers);
      if (error.response.status === 401) {
        //   setTimeout(() => {
        //     Actions.reset('login');
        //     store.dispatch(clearSession());
        //   }, 200);
      }
    }
    // 429: add retryAfter (+ a friendly message when the language is known) so
    // callers can show "try again in n seconds" and pollers can back off.
    return Promise.reject(annotateRateLimitResponse(error.response, options.ctx) || error);
  };

  return client(options).then(onSuccess).catch(onError);
};

export default request;
